import React from "react";
import { createRoot } from "react-dom/client";

// Import polyfills. Babel will read our `browserslist` entry and change this into imports for the
// specific polyfills needed to support our target browsers. This works because react-scripts
// configures @babel/preset-env to do so.
// @see https://github.com/facebook/create-react-app/blob/8f03b910441ac7de8e07d701a95a815886f9b31b/packages/babel-preset-react-app/dependencies.js#L86
import "core-js";

import "bootstrap/dist/css/bootstrap.min.css";
import "focus-visible";

import ConnectionsProvider from "graphql/ConnectionsProvider";
import AudioRecorder from "audio-recorder-polyfill";

import Application from "pages/Application";
import ErrorBoundary from "ui/ErrorBoundary";
import ErrorPage from "pages/Application/ErrorPage";
import ThemeProvider from "ui/theme";
import { GetHelpProvider } from "components/GetHelpModal/GetHelpContext";
import GetHelpButton from "components/GetHelpModal/GetHelpButton";

import {
  initializeSegmentAnonymousId,
  setupAnalyticsMiddleware,
} from "utils/analytics";
import ConsentManager from "utils/ConsentManager";
import setupAnalyticsConsoleLogger from "utils/setupAnalyticsConsoleLogger";
import appConfig from "utils/appConfig";
import {
  envWithDefault,
  envWithThrowIfMissing,
  featureEnabled,
  alongEnv,
  setEnvOverrides,
} from "utils/env";
import setupAppcues from "setupAppcues";

import * as Sentry from "@sentry/browser";
import axios from "axios";
import { ExperimentProvider } from "helpers/splitio/ExperimentContext";
import { AuthProvider } from "graphql/AuthContext";

const environment = alongEnv();
if (!environment) {
  throw new Error(`client expects environment to be set`);
}

const overrides = new URLSearchParams(window.location.search).get("overrides");
if (overrides && environment !== "production") {
  setEnvOverrides(JSON.parse(overrides));
}

const SENTRY_DSN = envWithThrowIfMissing("SENTRY_BROWSER_DSN");
const SENTRY_RELEASE_NAME =
  process.env.REACT_APP_SOURCE_VERSION || "development";

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE_NAME,
    environment,
    normalizeDepth: 10,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",

      // Microsoft "Safe Links" feature scrapes websites from links in Outlook
      // emails. That "browser" creates errors with this message, which comes
      // from the link below, a library for .NET Chrome bindings. We never want
      // to report this.
      // https://github.com/cefsharp/CefSharp/blob/master/CefSharp/Internals/JavascriptObjectRepository.cs#L293
      "Object Not Found Matching Id:",

      // On OSX and iOS Safari (which means all iOS browsers), there are edge
      // cases where clicking play or scrubbing a video player in various states,
      // most likely when the video hasnt loaded yet, can result in a rejected
      // promise that we dont care about because it does not appear to impact
      // the user experience. We cant figure out how to actually catch this, so
      // just filter it out at the client before it makes more Sentry incidents.
      "AbortError: The operation was aborted.",
    ],
  });
}

async function redirectToAuthIfNeeded() {
  if (featureEnabled("ENABLE_SSO_OKTA_AUTH")) {
    try {
      const response = await axios.get("/sso/auths/verified");
      const { is_sso_verified } = response.data;
      if (!is_sso_verified) {
        window.location.href = "/sso/auth";
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}

redirectToAuthIfNeeded();

window.isMediaRecorderPolyfilledForAudio = false;
if (!window.MediaRecorder) {
  /**
   * Some browsers, like Safari, don't support the MediaRecorder API yet: https://bugs.webkit.org/show_bug.cgi?id=85851
   * If a browser doesn't, polyfill ONLY for Audio. If an user wants to record video, their browser should still use the
   * input file flow, as if the polyfill didn't happen.
   */
  window.MediaRecorder = AudioRecorder;
  window.isMediaRecorderPolyfilledForAudio = true;
}

if (
  (envWithDefault("REACT_APP_SEGMENT_WRITE_KEY") ||
    environment === "production") &&
  window.analytics
) {
  const analytics = window.analytics;
  const segmentWriteKey = envWithThrowIfMissing("REACT_APP_SEGMENT_WRITE_KEY");

  analytics.load(segmentWriteKey, {
    integrations: ConsentManager.integrations(),
  });

  ConsentManager.onIntegrationsChange((integrations) => {
    // if cookie settings were changed, run analytics.load() again.
    // segment will take care of loading missing integrations.
    analytics.load(segmentWriteKey, { integrations });
  });

  initializeSegmentAnonymousId();

  setupAnalyticsMiddleware(analytics);

  if (appConfig.showAnalyticsLog()) {
    setupAnalyticsConsoleLogger(analytics);
  }
}

const appcuesId = envWithDefault("APPCUES_ID");
const enableAppcues = featureEnabled("ENABLE_APPCUES");
// Load Appcues script once
if (enableAppcues && appcuesId) {
  setupAppcues(window, "Appcues", appcuesId);
}

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <ErrorBoundary component="Application" fallback={ErrorPage}>
        <AuthProvider>
          <ConnectionsProvider>
            <ExperimentProvider>
              <GetHelpProvider>
                <Application />
                <GetHelpButton />
              </GetHelpProvider>
            </ExperimentProvider>
          </ConnectionsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>,
);
